import isEqual from 'lodash.isequal';

import Logger from 'wanderu-logger';

import * as i18nMoment from '../../libs/i18nMoment';
import { I18nMoney } from '../../libs/i18nMoney';
import { I18nNumber } from '../../libs/i18nNumber';
import { init as initLocalize } from '../../localize';

const logger = Logger.getLogger('LocaleCoordinator');
function _noop() {}

class LocaleCoordinator {
    constructor(i18nDataStore, i18next) {
        this._localeChangeHandle = undefined;
        this._onSupportedLocalesChange = _noop;
        this._supportedLocales = [];
        this.i18nDataStore = i18nDataStore;
        this.i18next = i18next;
    }

    init(opts) {
        const {
            defaultLocale,
            locale,
        } = opts;

        this.i18next.init({ locale });
        initLocalize(this.i18next);
        this.i18nDataStore.setLocale(locale);
        this.i18nDataStore.setDefaultLocale(defaultLocale);
        i18nMoment.setLocale(locale);
        I18nMoney.setLocale(locale);
        I18nNumber.setLocale(locale);
    }

    hydrate(translations) {
        if (translations) {
            Object.entries(translations).forEach(([locale, localeTranslations]) => {
                Object.entries(localeTranslations)
                    .forEach(([namespace, appTranslations]) => {
                        this.i18next.loadTranslationStrings(locale, appTranslations, namespace);
                    });
            });
        }

        const locale = this.i18nDataStore.getLocale();

        this.i18next.setLocale(locale);
        i18nMoment.setLocale(locale);
        I18nMoney.setLocale(locale);
        I18nNumber.setLocale(locale);
    }

    updateTranslations(locale, translations, namespace) {
        this.i18next.loadTranslationStrings(locale, translations, namespace, false);
        this.notifyChangeListeners(locale, locale);
    }

    async initI18nLibraries(locale) {
        try {
            this._assertAllAppsSupportLocale(locale);
            await i18nMoment.fetchLocaleData(locale);
        } catch (ex) {
            logger.warn('initI18nLibraries', ex);
        }
    }

    registerListeners(opts) {
        const {
            onLocaleChange = _noop,
            onSupportedLocalesChange = _noop,
        } = opts;

        this._localeChangeHandle = this.i18nDataStore
            .addLocaleChangeListener(onLocaleChange);
        this._onSupportedLocalesChange = onSupportedLocalesChange;
    }

    unregisterListeners() {
        this.i18nDataStore
            .removeLocaleChangeListener(this._localeChangeHandle);
        this._onSupportedLocalesChange = _noop;
    }

    async update(appConfig) {
        this._updateSupportedLocales();

        const locale = this.i18nDataStore.getLocale();

        try {
            this._assertAllAppsSupportLocale(locale);
        } catch (ex) {
            logger.warn('update localeCoordinator', ex.message);
            this._notifyLocaleChange(appConfig);

            return;
        }

        if (appConfig && !this.i18next.hasResourceBundle(locale, appConfig.namespace)) {
            await this._loadAppTranslations(appConfig);

            this.notifyChangeListeners(locale, locale);
        }
    }

    async updateLocale(locale, { silent = false } = {}) {
        const currentLocale = this.i18nDataStore.getLocale();

        if (locale === currentLocale) {
            logger.info('updateLocale', `${locale} is already the current locale`);
            return;
        }

        try {
            this._assertAllAppsSupportLocale(locale);
            await this._loadAllAppTranslations(locale);
        } catch (ex) {
            throw ex;
        }

        try {
            await i18nMoment.fetchLocaleData(locale);
            i18nMoment.setLocale(locale);
        } catch (ex) {
            // TS: We should what some UX to notify users of this case
            logger.warn('updateLocale:moment', ex);
        } finally {
            this.i18nDataStore.setLocale(locale);
            this.i18next.setLocale(locale);
            I18nNumber.setLocale(locale);
            I18nMoney.setLocale(locale);

            if (silent) {
                return;
            }

            this.notifyChangeListeners(locale, currentLocale);
        }
    }

    async _notifyLocaleChange(appConfig) {
        const compatibleLocale = this._getCompatibleLocale(appConfig);
        await this.updateLocale(compatibleLocale);
    }

    notifyChangeListeners(locale, prevLocale, optionalCurrency) {
        const changeListeners = this.i18nDataStore.getLocaleChangeListeners();

        Object.entries(changeListeners).forEach(([handle, listener]) => {
            // Listeners can cause other listeners to unregister
            // and getLocaleChangeListeners returns an immutable object
            if (!this.i18nDataStore.localeChangeListenerExists(handle)) {
                return;
            }

            listener(locale, prevLocale, optionalCurrency);
        });
    }

    _getCompatibleLocale(appConfig) {
        // Try current locale
        const locale = this.i18nDataStore.getLocale();
        try {
            this._assertAllAppsSupportLocale(locale);
            return locale;
        } catch (ex) {
            logger.warn('_getCompatibleLocale', ex.message);
        }

        // Try app default locale
        if (appConfig && appConfig.defaultLocale) {
            try {
                this._assertAllAppsSupportLocale(appConfig.defaultLocale);
                // get fallbackLocale
                // if (!fallbackLocale)
                return appConfig.defaultLocale;
            } catch (ex) {
                logger.warn('_getCompatibleLocale', ex.message);
            }
        }

        // Try gobal default locale
        try {
            const defaultLocale = this.i18nDataStore.getDefaultLocale();
            this._assertAllAppsSupportLocale(defaultLocale);
            return defaultLocale;
        } catch (ex) {
            logger.warn('_getCompatibleLocale', ex.message);
        }

        // Return first locale in supported locales
        return this._supportedLocales[0];
    }

    _updateSupportedLocales() {
        const prevSupportedLocales = this._supportedLocales;
        this._supportedLocales = this._getIntersectionOfSupportedLocales();

        const sameSupportedLocales = isEqual(this._supportedLocales, prevSupportedLocales);

        if (sameSupportedLocales) {
            return;
        }

        this._onSupportedLocalesChange(this._supportedLocales);
    }

    _getIntersectionOfSupportedLocales() {
        const appConfigs = this.i18nDataStore.getAppConfigs();
        if (Object.keys(appConfigs).length === 0) {
            return appConfigs;
        }

        return Object.values(appConfigs)
            .reduce((currentSet, appConfig) => {
                const appSupportedLocales = appConfig.supportedLocales;

                if (currentSet.length === 0) {
                    return [...appSupportedLocales];
                }

                return currentSet.filter((locale) => {
                    return appSupportedLocales.some((appSupportedLocale) => {
                        return locale === appSupportedLocale;
                    });
                });
            }, []);
    }

    _assertAllAppsSupportLocale(locale) {
        const appConfigs = this.i18nDataStore.getAppConfigs();

        const appNamesThatDoNotSupportLocale = Object.values(appConfigs)
            .reduce((appNames, appConfig) => {
                const localeNotSupported = !appConfig.supportedLocales.includes(locale);

                if (localeNotSupported) {
                    return [...appNames, appConfig.namespace];
                }

                return appNames;
            }, []);

        if (appNamesThatDoNotSupportLocale.length > 0) {
            throw new Error(`${appNamesThatDoNotSupportLocale} do(es) not support ${locale}`);
        }
    }

    async _loadAllAppTranslations(locale) {
        const appConfigs = this.i18nDataStore.getAppConfigs();

        const translationPairs = await Promise.all(
            Object.values(appConfigs)
                .filter((appConfig) => {
                    const { namespace } = appConfig;
                    return !this.i18next.hasResourceBundle(locale, namespace);
                })
                .map(async (appConfig) => {
                    const translations = await this._fetchTranslations(locale, appConfig);
                    return [appConfig, translations];
                }),
        );

        translationPairs.forEach(([appConfig, translations]) => {
            this._loadTranslations(locale, appConfig, translations);
        });
    }

    async _loadAppTranslations(appConfig) {
        const locale = this.i18nDataStore.getLocale();
        const translations = await this._fetchTranslations(locale, appConfig);
        this._loadTranslations(locale, appConfig, translations);
    }

    async refetchTranslations(namespace) {
        const locale = this.i18nDataStore.getLocale();
        const appConfig = this.i18nDataStore.getAppConfig(namespace);
        const newTranslations = await this._fetchTranslations(locale, appConfig);

        this.updateTranslations(locale, newTranslations, appConfig.namespace);
    }

    async _fetchTranslations(locale, appConfig) {
        try {
            return await appConfig.translationFetchFn(locale);
        } catch (ex) {
            logger.info(appConfig.namespace, ex);
            throw new Error(
                `App ${appConfig.namespace} does not have translation files for ${locale}`,
            );
        }
    }

    _loadTranslations(locale, appConfig, translations) {
        const { namespace } = appConfig;
        this.i18nDataStore.setAppTranslations(locale, namespace, translations);

        this.i18next.loadTranslationStrings(locale, translations, namespace);

        // Right now localize() needs to know about translation resource
        // change but only listens to localeChange.
        // Eventually localize should be refactored to listen to changes
        // in resources over locale and this will be unnecessary
    }
}

export default LocaleCoordinator;
